// Is the viewer a platform admin?
//
// This hides a BUTTON or a control — it is not a security boundary. Reading one's
// own roles is allowed by RLS ("Users can read own roles").
//
// ── WHY NOT useIsAdmin ───────────────────────────────────────────────────
// That hook needs React Query and a static import of the Supabase client. The
// homepage is prerendered under Node, where that client throws at import, and
// the barn leaderboard document mounts no QueryClient. So this reads the
// session the way SignedInBadge does: a DYNAMIC import inside an effect, which
// never runs during the prerender.
//
// And it only does that when the browser holds a Supabase session at all, so a
// signed-out visitor — nearly everyone — downloads nothing extra.
//
// Fails CLOSED: until an admin row is confirmed, the answer is false.

import { useEffect, useState } from "react";

/** A stored Supabase session: `sb-<project>-auth-token` in localStorage. */
function hasStoredSession(): boolean {
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && /^sb-.+-auth-token$/.test(key)) return true;
    }
  } catch {
    /* storage blocked: treat as signed out */
  }
  return false;
}

export function useViewerIsAdmin(): boolean {
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    if (!hasStoredSession()) return;
    let live = true;
    let unsubscribe: (() => void) | null = null;

    void import("@/integrations/supabase/client")
      .then(({ supabase }) => {
        const check = async (userId: string | null) => {
          if (!userId) {
            if (live) setIsAdmin(false);
            return;
          }
          const { data, error } = await supabase
            .from("user_roles")
            .select("role")
            .eq("user_id", userId)
            .eq("role", "admin")
            .maybeSingle();
          if (live) setIsAdmin(!error && !!data);
        };

        void supabase.auth.getSession().then(({ data }) => check(data.session?.user?.id ?? null));
        // Deferred: calling Supabase from inside the auth callback can deadlock.
        const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
          setTimeout(() => void check(session?.user?.id ?? null), 0);
        });
        unsubscribe = () => sub.subscription.unsubscribe();
        if (!live) unsubscribe();
      })
      .catch(() => {
        /* no session module: no Share button */
      });

    return () => {
      live = false;
      unsubscribe?.();
    };
  }, []);

  return isAdmin;
}
